// ═══════════════════════════════════════════════════════════════════════════
// TENANT-CONFIG — l'identité de L'INSTANCE, en un seul point (lot I-A, 05/09/2026).
//
// Mesuré le 05/09 : 1 175 occurrences de « aureussocial.be » dans 463 fichiers,
// dont 30 replis `TENANT.appUrl`
// et 6 expéditeurs `from: TENANT.emailNoreply` en dur. Louer le logiciel à
// un autre gestionnaire de paie (chemin A du runbook 40.25) exigeait de relire
// 463 fichiers — et le licencié aurait envoyé des mails signés Aureus.
//
// LA FORME : des variables d'environnement, avec Aureus en DÉFAUT. Sans aucune
// variable TENANT_*, cette instance est Aureus à l'octet près — la première
// tranche ne change rien en production, elle déplace la source. Une instance
// licenciée pose ses variables dans Vercel et hérite du reste.
//
// ⚠️ `process.env.NEXT_PUBLIC_*` s'écrit en clair, jamais via une clé dynamique :
// Next.js n'inline côté navigateur que les accès littéraux.
//
// 3e tranche (05/09/2026 soir) — CLIENT-SAFE : ce module est aussi importé par des écrans
// 'use client'. Côté navigateur, `process.env.TENANT_*` et `EMAIL_FROM` n'existent PAS (seules
// les NEXT_PUBLIC_* sont inlinées au build) → sans cette tranche un écran aurait rendu le
// défaut Aureus sur une instance licenciée, FAUX en silence. Ordre de lecture, pour chaque
// champ d'identité : NEXT_PUBLIC_TENANT_X (lu partout) > variable serveur historique > défaut.
// Un licencié pose donc les NEXT_PUBLIC_TENANT_* — une fois, pour les deux côtés.
//
// Ce fichier ne remplace pas `societe-interne.js` (qui dit « ce dossier client
// est-il NOTRE société ? ») : il en lit le BCE par défaut. Verrous :
// __tests__/tenant-config.test.js (exécute) · __tests__/identite-instance-cliquet.test.js
// (balaie : le compte d'occurrences en dur ne peut que baisser).
// ═══════════════════════════════════════════════════════════════════════════
import { BCE_SOCIETE_INTERNE, NOM_SOCIETE_INTERNE } from './societe-interne.js'; // relatif : lu en Node brut via agents-registry (scripts/smoke-agents.mjs), pas d'alias @/

const nonVide = (v, defaut) => (typeof v === 'string' && v.trim() ? v.trim() : defaut);
const sansSlashFinal = (u) => String(u || '').replace(/\/+$/, '');

export const TENANT = Object.freeze({
  /** Nom du produit tel qu'il apparaît à l'écran et dans les mails. */
  nom: nonVide(process.env.NEXT_PUBLIC_TENANT_NOM, nonVide(process.env.TENANT_NOM, 'Aureus Social Pro')),
  /** Raison sociale de l'exploitant de l'instance. */
  societe: nonVide(process.env.NEXT_PUBLIC_TENANT_SOCIETE, nonVide(process.env.TENANT_SOCIETE, NOM_SOCIETE_INTERNE)),
  /** BCE de l'exploitant, chiffres seuls. */
  bce: nonVide(process.env.NEXT_PUBLIC_TENANT_BCE, nonVide(process.env.TENANT_BCE, BCE_SOCIETE_INTERNE)).replace(/\D/g, ''),
  /** URL de l'application (liens dans les mails, réarmement des crons, webhooks). */
  appUrl: sansSlashFinal(nonVide(process.env.NEXT_PUBLIC_APP_URL, 'https://app.aureussocial.be')),
  /** URL du site public (blog, pages commerciales). */
  siteUrl: sansSlashFinal(nonVide(process.env.NEXT_PUBLIC_SITE_URL, 'https://www.aureussocial.be')),
  /** Expéditeur principal (adresse nue) et sa forme libellée « Nom <adresse> ». */
  emailFrom: nonVide(process.env.NEXT_PUBLIC_TENANT_EMAIL_FROM, nonVide(process.env.EMAIL_FROM, 'info@aureussocial.be')),
  /** Expéditeur des envois automatiques sans réponse attendue. */
  emailNoreply: nonVide(process.env.NEXT_PUBLIC_TENANT_EMAIL_NOREPLY, nonVide(process.env.TENANT_EMAIL_NOREPLY, 'noreply@aureussocial.be')),
  /** Expéditeur des rapports (client-report). */
  emailRapports: nonVide(process.env.NEXT_PUBLIC_TENANT_EMAIL_RAPPORTS, nonVide(process.env.TENANT_EMAIL_RAPPORTS, 'rapports@aureussocial.be')),
});

/** Dérivés — jamais posés à la main, toujours calculés (4e tranche, 05/09/2026) :
 *  domaine  : la partie droite de l'expéditeur (« aureussocial.be ») — sert aux adresses
 *             opérationnelles des robots (`monitoring@${domaine}`, `backup@…`) ;
 *  appHote  : le nom d'hôte de l'app (« app.aureussocial.be »), tel qu'on l'imprime en pied
 *             de PDF ou en signature de mail ;
 *  siteHote : le nom d'hôte du site public (« www.aureussocial.be »). */
const hote = (u) => { try { return new URL(u).hostname; } catch { return String(u || '').replace(/^https?:\/\//, '').split('/')[0]; } };
export const TENANT_DERIVE = Object.freeze({
  domaine: String(TENANT.emailFrom.split('@')[1] || hote(TENANT.appUrl).replace(/^app\./, '')),
  appHote: hote(TENANT.appUrl),
  siteHote: hote(TENANT.siteUrl),
});

/** « Nom <adresse> » — la forme que Resend affiche. */
export const EMAIL_FROM_LIBELLE = `${TENANT.nom} <${TENANT.emailFrom}>`;

/** Cette instance est-elle celle d'Aureus ? (le canal ONSS d'Aureus ne doit servir qu'ici — IA3) */
export function estInstanceAureus(t = TENANT) {
  return t.bce === BCE_SOCIETE_INTERNE && /aureussocial\.be$/.test(new URL(t.appUrl).hostname);
}

/** Une URL absolue de l'app : appUrl('/?page=payslip') → 'https://…/?page=payslip'. */
export function appUrl(chemin = '') {
  const c = String(chemin || '');
  return c ? `${TENANT.appUrl}${c.startsWith('/') ? '' : '/'}${c}` : TENANT.appUrl;
}
